/* eslint-disable @typescript-eslint/no-unsafe-return */

import {Fragment, FunctionalComponent, h} from "@stencil/core";
import {i18nProvider} from "@trumpf/ux.i18n";

const {t} = i18nProvider;

export const Topic: FunctionalComponent<{
	headlineSlot: string;
	textSlot: string;
}> = ({headlineSlot, textSlot}) => (
	<div class="u-marginBottom-40">
		<slot name={headlineSlot} />
		<div class="text-normal">
			<slot name={textSlot} />
		</div>
	</div>
);

export const TableHeader: FunctionalComponent<{headerSlot: string}> = ({headerSlot}) => (
	<th>
		<slot name={headerSlot} />
	</th>
);

export const TableIconHeader: FunctionalComponent<{
	headerSlot: string;
	icon: "square-info" | "triangle-warning";
	tooltip: string;
}> = ({headerSlot, icon, tooltip}) => (
	<th>
		<slot name={headerSlot} />
		<ux-icon name={icon} weight="bold" style={{color: icon === "triangle-warning" ? "red" : "inherit"}} />
		<ux-tooltip tooltip-target={`ux-icon[name=${icon}]`} align="top">
			{tooltip}
		</ux-tooltip>
	</th>
);

export const TextCell: FunctionalComponent<{
	text: string;
	info?: {
		icon: "square-info" | "triangle-warning";
		tooltip: string;
	};
}> = ({text, info}) => (
	<td>
		<span>{text}</span>
		{info && (
			<Fragment>
				<ux-icon style={{color: info.icon === "triangle-warning" ? "red" : "inherit"}} name={info.icon} weight="bold" />
				<ux-tooltip tooltip-target={`ux-icon[name=${info.icon}]`} align="top">
					{info.tooltip}
				</ux-tooltip>
			</Fragment>
		)}
	</td>
);

export const CheckboxCell: FunctionalComponent<{
	name?: string;
	label?: string;
	disabled?: boolean;
	required?: boolean;
	isChecked: boolean;
	onChange: (e: Event) => void;
}> = ({name, label = "", disabled = false, required = false, isChecked, onChange}) => (
	<td>
		<ux-checkbox name={name} {...(label !== "" && {label})} disabled={disabled} required={required} checked={isChecked} onChange={onChange} />
	</td>
);

export const TableSkeleton: FunctionalComponent<{
	columns?: number;
	rows?: number;
	noAnimation?: boolean;
}> = ({columns = 3, rows = 2, noAnimation = false}) => (
	<Fragment>
		{Array.from({length: rows}, (_, rowIndex) => (
			<tr key={rowIndex} class={"skeleton"}>
				{Array.from({length: columns}, (_, columnIndex) => (
					<td key={columnIndex} class={noAnimation ? "no-animation" : ""} />
				))}
			</tr>
		))}
	</Fragment>
);

type EditFooterProps = {
	saveButtonSlot: string;
	cancelButtonSlot: string;
	hasChanges: boolean;
	hasFailedLoading: boolean;
	updateCallback: () => Promise<void>;
	resetCallback: () => void;
};

export const EditFooter: FunctionalComponent<EditFooterProps> = ({
	saveButtonSlot,
	cancelButtonSlot,
	hasChanges,
	hasFailedLoading,
	updateCallback,
	resetCallback,
}) => (
	<footer class="ux-button-group-spacing">
		<button class={`ux-button ux-button--green ${!hasChanges && "ux-button--disabled"}`} disabled={!hasChanges} onClick={() => void updateCallback()}>
			<slot name={saveButtonSlot} />
		</button>
		<button class="ux-button" style={{display: hasChanges ? "inline-block" : "none"}} onClick={resetCallback}>
			<slot name={cancelButtonSlot} />
		</button>
		{hasFailedLoading && (
			<Fragment>
				<ux-icon name="circle-cross" class="ux-color-error" sizeL="25" weight="bold" />
				<ux-tooltip tooltip-target="ux-icon[name='circle-cross']">{t("user-settings:genericError")}</ux-tooltip>
			</Fragment>
		)}
	</footer>
);
